export interface KanbanData {
  name: string;
  columns: {
    id: string;
    title: string;
    color: string;
  }[];
  items: {
    id: string;
    title: string;
    description?: string;
    columnId: string;
    order: number;
    submissionId?: string;
    contentPlanEventId?: string;
  }[];
}

export class KanbanBlock {
  // Required by Editor.js interface
  // @ts-expect-error - Required by Editor.js interface
  private _api: any;
  // @ts-expect-error - Required by Editor.js interface
  private _readOnly: boolean;
  private data: KanbanData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Kanban Board',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><rect width="14" height="14" x="5" y="5" stroke="currentColor" stroke-width="2" rx="2"></rect><line x1="9" x2="9" y1="3" y2="21" stroke="currentColor" stroke-width="2"></line><line x1="15" x2="15" y1="3" y2="21" stroke="currentColor" stroke-width="2"></line><line x1="3" x2="21" y1="9" y2="9" stroke="currentColor" stroke-width="2"></line><line x1="3" x2="21" y1="15" y2="15" stroke="currentColor" stroke-width="2"></line></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, api, readOnly }: any) {
    this._api = api;
    this._readOnly = readOnly;
    
    const defaultData = {
      name: 'Kanban Board',
      columns: [
        { id: 'video_ideas', title: 'Video Ideas', color: '#3b82f6' },
        { id: 'videos_in_production', title: 'Videos In Production', color: '#f59e0b' },
        { id: 'videos_posted', title: 'Videos Posted', color: '#10b981' }
      ],
      items: [
        // Video Ideas (6 items)
        { id: 'item_1', title: 'Item 1', description: '', columnId: 'video_ideas', order: 0 },
        { id: 'item_2', title: 'Item 2', description: '', columnId: 'video_ideas', order: 1 },
        { id: 'item_3', title: 'Item 3', description: '', columnId: 'video_ideas', order: 2 },
        { id: 'item_4', title: 'Item 4', description: '', columnId: 'video_ideas', order: 3 },
        { id: 'item_5', title: 'Item 5', description: '', columnId: 'video_ideas', order: 4 },
        { id: 'item_6', title: 'Item 6', description: '', columnId: 'video_ideas', order: 5 },
        // Videos In Production (4 items)
        { id: 'item_7', title: 'Item 7', description: 'Could be linked to a submission of user', columnId: 'videos_in_production', order: 0 },
        { id: 'item_8', title: 'Item 8', description: 'Could be linked to a submission of user', columnId: 'videos_in_production', order: 1 },
        { id: 'item_9', title: 'Item 9', description: '', columnId: 'videos_in_production', order: 2 },
        { id: 'item_10', title: 'Item 10', description: '', columnId: 'videos_in_production', order: 3 },
        // Videos Posted (2 items)
        { id: 'item_11', title: 'Item 11', description: 'Could be linked to an event in content plan', columnId: 'videos_posted', order: 0 },
        { id: 'item_12', title: 'Item 12', description: 'Could be linked to an event in content plan', columnId: 'videos_posted', order: 1 }
      ]
    };
    
    this.data = data || defaultData;
    
    // If data is passed but items array is empty, use default items
    if (data && (!data.items || data.items.length === 0)) {
      this.data.items = defaultData.items;
    }
    
    // If data is passed but columns array is empty, use default columns
    if (data && (!data.columns || data.columns.length === 0)) {
      this.data.columns = defaultData.columns;
    }
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('kanban-block');
    
    this.renderKanban();
    
    return this.wrapper;
  }

  renderKanban() {
    if (!this.wrapper) return;

    // Ensure data is properly initialized
    if (!this.data.columns) {
      this.data.columns = [
        { id: 'video_ideas', title: 'Video Ideas', color: '#3b82f6' },
        { id: 'videos_in_production', title: 'Videos In Production', color: '#f59e0b' },
        { id: 'videos_posted', title: 'Videos Posted', color: '#10b981' }
      ];
    }
    if (!this.data.items) {
      this.data.items = [];
    }

    this.wrapper.innerHTML = `
      <div class="kanban-columns"></div>
    `;

    const columnsContainer = this.wrapper.querySelector('.kanban-columns') as HTMLElement;

    // Force horizontal layout with inline styles
    if (columnsContainer) {
      columnsContainer.style.display = 'flex';
      columnsContainer.style.flexDirection = 'row';
      columnsContainer.style.flexWrap = 'nowrap';
      columnsContainer.style.gap = '1rem';
      columnsContainer.style.overflowX = 'auto';
      columnsContainer.style.paddingBottom = '1rem';
    }

    // Render columns
    this.data.columns.forEach((column: any) => {
      this.renderColumn(column, columnsContainer);
    });
  }

  renderColumn(column: any, container: Element | null) {
    if (!container) return;

    const columnEl = document.createElement('div');
    columnEl.classList.add('kanban-column');
    columnEl.dataset.columnId = column.id;
    columnEl.style.borderLeft = `3px solid ${column.color}`;
    columnEl.style.display = 'inline-block';
    columnEl.style.verticalAlign = 'top';
    columnEl.style.width = '288px';
    columnEl.style.flexShrink = '0';

    columnEl.innerHTML = `
      <div class="kanban-column-header">
        <input type="text" class="kanban-column-title" value="${column.title}" placeholder="Column Title" />
        <button class="kanban-add-item-btn" title="Add Item">+</button>
        <button class="kanban-delete-column-btn" title="Delete Column">×</button>
      </div>
      <div class="kanban-column-items"></div>
    `;

    const itemsContainer = columnEl.querySelector('.kanban-column-items');
    const titleInput = columnEl.querySelector('.kanban-column-title');
    const addItemBtn = columnEl.querySelector('.kanban-add-item-btn');
    const deleteColumnBtn = columnEl.querySelector('.kanban-delete-column-btn');

    // Render items for this column
    const columnItems = (this.data.items || []).filter((item: any) => item.columnId === column.id);
    columnItems.sort((a: any, b: any) => a.order - b.order);
    columnItems.forEach((item: any) => {
      this.renderItem(item, itemsContainer);
    });

    // Title input handler
    titleInput?.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      column.title = target.value;
    });

    // Add item button handler
    addItemBtn?.addEventListener('click', () => {
      const newItem = {
        id: `item_${Date.now()}`,
        title: 'New Item',
        description: '',
        columnId: column.id,
        order: (this.data.items || []).filter((i: any) => i.columnId === column.id).length
      };
      if (!this.data.items) {
        this.data.items = [];
      }
      this.data.items.push(newItem);
      this.renderItem(newItem, itemsContainer);
    });

    // Delete column button handler
    deleteColumnBtn?.addEventListener('click', () => {
      this.data.columns = (this.data.columns || []).filter((c: any) => c.id !== column.id);
      this.data.items = (this.data.items || []).filter((i: any) => i.columnId !== column.id);
      columnEl.remove();
    });

    container.appendChild(columnEl);
  }

  renderItem(item: any, container: Element | null) {
    if (!container) return;

    const itemEl = document.createElement('div');
    itemEl.classList.add('kanban-item');
    itemEl.draggable = true;
    itemEl.dataset.itemId = item.id;

    itemEl.innerHTML = `
      <input type="text" class="kanban-item-title" value="${item.title}" placeholder="Item Title" />
      <textarea class="kanban-item-description" placeholder="Description">${item.description || ''}</textarea>
      <button class="kanban-delete-item-btn" title="Delete Item">×</button>
    `;

    const titleInput = itemEl.querySelector('.kanban-item-title');
    const descriptionInput = itemEl.querySelector('.kanban-item-description');
    const deleteItemBtn = itemEl.querySelector('.kanban-delete-item-btn');

    // Title input handler
    titleInput?.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      item.title = target.value;
    });

    // Description input handler
    descriptionInput?.addEventListener('input', (e) => {
      const target = e.target as HTMLTextAreaElement;
      item.description = target.value;
    });

    // Delete item button handler
    deleteItemBtn?.addEventListener('click', () => {
      this.data.items = (this.data.items || []).filter((i: any) => i.id !== item.id);
      itemEl.remove();
    });

    // Drag and drop handlers
    itemEl.addEventListener('dragstart', (e) => {
      itemEl.classList.add('dragging');
      e.dataTransfer?.setData('text/plain', item.id);
    });

    itemEl.addEventListener('dragend', () => {
      itemEl.classList.remove('dragging');
    });

    // Click handler to open card detail modal
    itemEl.addEventListener('click', (e) => {
      if (e.target !== deleteItemBtn && !deleteItemBtn?.contains(e.target as Node)) {
        this.openCardModal(item);
      }
    });

    container.appendChild(itemEl);
  }

  openCardModal(item: any) {
    const modal = document.createElement('div');
    modal.classList.add('kanban-card-modal');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    modal.innerHTML = `
      <div class="kanban-card-modal-content" style="
        background: #1f2937;
        border: 1px solid #374151;
        border-radius: 0.75rem;
        padding: 1.5rem;
        max-width: 500px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
      ">
        <div class="kanban-card-modal-header" style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #374151;
        ">
          <h3 style="color: white; font-size: 1.25rem; font-weight: 600; margin: 0;">Card Details</h3>
          <button class="kanban-card-modal-close" style="
            background: #374151;
            color: white;
            border: none;
            border-radius: 0.375rem;
            padding: 0.5rem 1rem;
            cursor: pointer;
            font-size: 0.875rem;
          ">Close</button>
        </div>
        <div class="kanban-card-modal-body">
          <div class="kanban-card-modal-field" style="margin-bottom: 1rem;">
            <label style="color: #9ca3af; font-size: 0.875rem; font-weight: 500; display: block; margin-bottom: 0.5rem;">Title</label>
            <input type="text" class="kanban-card-modal-title" value="${item.title}" style="
              background: #111827;
              color: white;
              border: 1px solid #374151;
              border-radius: 0.375rem;
              padding: 0.75rem;
              width: 100%;
              font-size: 0.875rem;
            " />
          </div>
          <div class="kanban-card-modal-field" style="margin-bottom: 1rem;">
            <label style="color: #9ca3af; font-size: 0.875rem; font-weight: 500; display: block; margin-bottom: 0.5rem;">Description</label>
            <textarea class="kanban-card-modal-description" style="
              background: #111827;
              color: white;
              border: 1px solid #374151;
              border-radius: 0.375rem;
              padding: 0.75rem;
              width: 100%;
              font-size: 0.875rem;
              min-height: 100px;
              resize: vertical;
            ">${item.description || ''}</textarea>
          </div>
          <div class="kanban-card-modal-field" style="margin-bottom: 1rem;">
            <label style="color: #9ca3af; font-size: 0.875rem; font-weight: 500; display: block; margin-bottom: 0.5rem;">Video Submission</label>
            <input type="text" class="kanban-card-modal-submission" value="${item.submissionId || ''}" placeholder="Search submissions..." style="
              background: #111827;
              color: white;
              border: 1px solid #374151;
              border-radius: 0.375rem;
              padding: 0.75rem;
              width: 100%;
              font-size: 0.875rem;
            " />
            <div class="kanban-card-modal-submission-dropdown" style="
              background: #111827;
              border: 1px solid #374151;
              border-radius: 0.375rem;
              margin-top: 0.5rem;
              max-height: 200px;
              overflow-y: auto;
              display: none;
            "></div>
          </div>
          <div class="kanban-card-modal-field" style="margin-bottom: 1rem;">
            <label style="color: #9ca3af; font-size: 0.875rem; font-weight: 500; display: block; margin-bottom: 0.5rem;">Content Plan Event ID</label>
            <input type="text" class="kanban-card-modal-content-plan-event" value="${item.contentPlanEventId || ''}" style="
              background: #111827;
              color: white;
              border: 1px solid #374151;
              border-radius: 0.375rem;
              padding: 0.75rem;
              width: 100%;
              font-size: 0.875rem;
            " />
          </div>
        </div>
        <div class="kanban-card-modal-footer" style="
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #374151;
        ">
          <button class="kanban-card-modal-save" style="
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.375rem;
            padding: 0.5rem 1rem;
            cursor: pointer;
            font-size: 0.875rem;
            font-weight: 500;
          ">Save</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const titleInput = modal.querySelector('.kanban-card-modal-title') as HTMLInputElement;
    const descriptionInput = modal.querySelector('.kanban-card-modal-description') as HTMLTextAreaElement;
    const submissionInput = modal.querySelector('.kanban-card-modal-submission') as HTMLInputElement;
    const contentPlanEventInput = modal.querySelector('.kanban-card-modal-content-plan-event') as HTMLInputElement;
    const closeBtn = modal.querySelector('.kanban-card-modal-close');
    const saveBtn = modal.querySelector('.kanban-card-modal-save');

    // Save handler
    saveBtn?.addEventListener('click', () => {
      item.title = titleInput.value;
      item.description = descriptionInput.value;
      item.submissionId = submissionInput.value;
      item.contentPlanEventId = contentPlanEventInput.value;
      
      // Update the item in the DOM
      const itemEl = this.wrapper?.querySelector(`[data-item-id="${item.id}"]`);
      if (itemEl) {
        const titleField = itemEl.querySelector('.kanban-item-title') as HTMLInputElement;
        const descField = itemEl.querySelector('.kanban-item-description') as HTMLTextAreaElement;
        if (titleField) titleField.value = item.title;
        if (descField) descField.value = item.description || '';
      }
      
      document.body.removeChild(modal);
    });

    // Close handler
    closeBtn?.addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  }

  save(blockContent: HTMLElement) {
    return this.data;
  }
}
